/**
 * Reviewed data and deterministic model for the Water Chemistry tool
 * (study-chemistry-tools-prd.md §8.3, topic "Water & materials", start Year 11).
 *
 * The `properties` mode explains water's high specific heat capacity, high enthalpy of
 * vaporisation, density maximum and floating ice with hydrogen bonding. The `quality` mode lives
 * in `waterChemistryQuality.ts`. All values are textbook reference values; nothing is measured.
 */
import { checkNumericAnswer, formatSig, type AnswerCheck } from './gasLaws';

export type WaterMode = 'properties' | 'quality';

/** Stable identity used by reviewed CurriculumToolBinding records. */
export const WATER_CHEMISTRY_TOOL_IDENTITY = {
  id: 'water-chemistry',
  toolKey: 'chemistry-water-chemistry',
  toolVersion: '1.0.0',
  lifecycle: 'live',
  computation: 'client-deterministic',
  evidencePolicy: 'none',
  masteryPolicy: 'not-assessed',
  curriculumPolicy: 'reviewed-binding-required',
} as const;

export const WATER_CHEMISTRY_START_YEAR = 11;
export const WATER_REVIEW_STATUS = 'pending-teaching-review' as const;

export const WATER_MODES: readonly WaterMode[] = ['properties', 'quality'];

/**
 * Both modes belong to the same Year 11 unit (§4.5). Properties is the foundation for the
 * quality mode, so it is the default for every Year; the learner can switch at any time.
 */
export const WATER_MODE_YEARS: readonly { mode: WaterMode; fromYear: number }[] = [
  { mode: 'properties', fromYear: WATER_CHEMISTRY_START_YEAR },
];

export interface WaterSource {
  label: string;
  detail: string;
}

export const WATER_PROPERTY_SOURCES: WaterSource[] = [
  {
    label: 'CRC Handbook of Chemistry and Physics',
    detail:
      'Specific heat capacities near 25 °C, enthalpies of vaporisation at the normal boiling point, density of liquid water 0–30 °C and of ice at 0 °C, boiling points of the group 16 hydrides.',
  },
  {
    label: 'IUPAC standard atomic weights',
    detail: 'Molar mass of water 18.02 g mol⁻¹.',
  },
];

export const WATER_PROPERTY_CONDITIONS =
  'Specific heat capacities are near 25 °C. Enthalpies of vaporisation are at each substance’s normal boiling point. Densities and boiling points are at 101.325 kPa.';

export const MOLAR_MASS_WATER = 18.02;
export const J_PER_KJ = 1000;
export const PERCENT = 100;

export interface SpecificHeatEntry {
  id: string;
  name: string;
  /** J g⁻¹ K⁻¹ near 25 °C. */
  value: number;
}

export const SPECIFIC_HEATS: SpecificHeatEntry[] = [
  { id: 'water', name: 'Water (liquid)', value: 4.18 },
  { id: 'ethanol', name: 'Ethanol (liquid)', value: 2.44 },
  { id: 'aluminium', name: 'Aluminium', value: 0.897 },
  { id: 'iron', name: 'Iron', value: 0.449 },
  { id: 'copper', name: 'Copper', value: 0.385 },
];

export const SPECIFIC_HEAT_WATER = 4.18;

export interface VaporisationEntry {
  id: string;
  formula: string;
  name: string;
  /** kJ mol⁻¹ at the normal boiling point. */
  enthalpyKJ: number;
  boilingPointC: number;
  hydrogenBonding: boolean;
}

export const VAPORISATION_ENTHALPIES: VaporisationEntry[] = [
  { id: 'water', formula: 'H₂O', name: 'Water', enthalpyKJ: 40.7, boilingPointC: 100, hydrogenBonding: true },
  { id: 'ethanol', formula: 'C₂H₅OH', name: 'Ethanol', enthalpyKJ: 38.6, boilingPointC: 78.3, hydrogenBonding: true },
  { id: 'ammonia', formula: 'NH₃', name: 'Ammonia', enthalpyKJ: 23.3, boilingPointC: -33.3, hydrogenBonding: true },
  { id: 'hydrogen-sulfide', formula: 'H₂S', name: 'Hydrogen sulfide', enthalpyKJ: 18.7, boilingPointC: -60.3, hydrogenBonding: false },
  { id: 'methane', formula: 'CH₄', name: 'Methane', enthalpyKJ: 8.19, boilingPointC: -161.5, hydrogenBonding: false },
];

export const VAPORISATION_ENTHALPY_WATER = 40.7;

export interface HydrideEntry {
  formula: string;
  molarMass: number;
  /** Normal boiling point, rounded to the nearest degree. */
  boilingPointC: number;
}

/** Group 16 hydrides in order of molar mass. */
export const GROUP16_HYDRIDES: HydrideEntry[] = [
  { formula: 'H₂O', molarMass: 18.02, boilingPointC: 100 },
  { formula: 'H₂S', molarMass: 34.08, boilingPointC: -60 },
  { formula: 'H₂Se', molarMass: 80.98, boilingPointC: -41 },
  { formula: 'H₂Te', molarMass: 129.6, boilingPointC: -2 },
];

export interface DensityPoint {
  temperatureC: number;
  /** g mL⁻¹ at 101.325 kPa. */
  density: number;
}

export const LIQUID_WATER_DENSITY: DensityPoint[] = [
  { temperatureC: 0, density: 0.99984 },
  { temperatureC: 2, density: 0.99994 },
  { temperatureC: 4, density: 0.99997 },
  { temperatureC: 6, density: 0.99994 },
  { temperatureC: 8, density: 0.99985 },
  { temperatureC: 10, density: 0.9997 },
  { temperatureC: 20, density: 0.99821 },
  { temperatureC: 30, density: 0.99565 },
];

export const ICE_DENSITY_0C = 0.9167;
export const DENSITY_MAXIMUM_C = 4;

export function densestLiquidPoint(points: DensityPoint[] = LIQUID_WATER_DENSITY): DensityPoint {
  return points.reduce((best, point) => (point.density > best.density ? point : best));
}

/** q = m c ΔT, in kJ. */
export function heatEnergyKJ(massG: number, specificHeat: number, deltaTK: number): number {
  return (massG * specificHeat * deltaTK) / J_PER_KJ;
}

/** ΔT = q ÷ (m c), with q in kJ. */
export function temperatureRiseK(energyKJ: number, massG: number, specificHeat: number): number {
  if (massG <= 0 || specificHeat <= 0) return Number.NaN;
  return (energyKJ * J_PER_KJ) / (massG * specificHeat);
}

/** Energy to vaporise a mass of water at 100 °C, in kJ. */
export function vaporisationEnergyKJ(massG: number): number {
  return (massG / MOLAR_MASS_WATER) * VAPORISATION_ENTHALPY_WATER;
}

export type WaterVisual = 'specific-heat' | 'vaporisation' | 'hydrides' | 'density';

export interface WaterChoiceOption {
  id: string;
  label: string;
  correct: boolean;
  feedback: string;
}

interface WaterCaseBase {
  id: string;
  title: string;
  prompt: string;
  visual: WaterVisual;
  /** Shown after any checked prediction. */
  takeaway: string;
}

export interface WaterChoiceCase extends WaterCaseBase {
  kind: 'choice';
  options: WaterChoiceOption[];
}

export interface WaterNumericMisconception {
  value: number;
  feedback: string;
}

export interface WaterNumericCase extends WaterCaseBase {
  kind: 'numeric';
  answerLabel: string;
  unit: string;
  answer: number;
  working: string[];
  misconceptions: WaterNumericMisconception[];
}

export type WaterPropertyCase = WaterChoiceCase | WaterNumericCase;

const HEAT_MASS_G = 250;
const HEAT_START_C = 20;
const HEAT_END_C = 80;
const HEAT_DELTA_T = HEAT_END_C - HEAT_START_C;
const RISE_ENERGY_KJ = 10;
const RISE_MASS_G = 500;
const VAPOUR_MASS_G = 36;
const ICE_FRACTION_PERCENT = (ICE_DENSITY_0C / LIQUID_WATER_DENSITY[0].density) * PERCENT;

export const WATER_PROPERTY_CASES: WaterPropertyCase[] = [
  {
    id: 'specific-heat-smallest-rise',
    kind: 'choice',
    title: 'Smallest temperature rise',
    prompt:
      'Equal 100 g samples of water, ethanol, aluminium and copper each absorb 1.00 kJ of energy. Which sample shows the smallest temperature rise?',
    visual: 'specific-heat',
    options: [
      { id: 'water', label: 'Water', correct: true, feedback: 'Water has the highest specific heat capacity here (4.18 J g⁻¹ K⁻¹), so the same energy raises its temperature least: ΔT = 1000 ÷ (100 × 4.18) = 2.4 °C.' },
      { id: 'copper', label: 'Copper', correct: false, feedback: 'Copper has the lowest specific heat capacity here (0.385 J g⁻¹ K⁻¹), so it shows the largest rise: about 26 °C. A low c means a big ΔT for the same energy.' },
      { id: 'aluminium', label: 'Aluminium', correct: false, feedback: 'Aluminium’s c is 0.897 J g⁻¹ K⁻¹, under a quarter of water’s, so it warms about 11 °C. Metals have low specific heat capacities.' },
      { id: 'ethanol', label: 'Ethanol', correct: false, feedback: 'Ethanol also hydrogen bonds, but its c (2.44 J g⁻¹ K⁻¹) is lower than water’s, so it warms about 4.1 °C — more than water.' },
    ],
    takeaway:
      'Water absorbs a lot of energy for a small temperature change because much of the energy goes into disrupting hydrogen bonds between molecules.',
  },
  {
    id: 'specific-heat-energy',
    kind: 'numeric',
    title: 'Heat a beaker of water',
    prompt: `How much energy, in kJ, is needed to heat ${HEAT_MASS_G} g of water from ${HEAT_START_C}.0 °C to ${HEAT_END_C}.0 °C?`,
    visual: 'specific-heat',
    answerLabel: 'q',
    unit: 'kJ',
    answer: heatEnergyKJ(HEAT_MASS_G, SPECIFIC_HEAT_WATER, HEAT_DELTA_T),
    working: [
      `ΔT = ${HEAT_END_C}.0 − ${HEAT_START_C}.0 = ${HEAT_DELTA_T}.0 K`,
      `q = m c ΔT = ${HEAT_MASS_G} g × 4.18 J g⁻¹ K⁻¹ × ${HEAT_DELTA_T}.0 K = ${formatSig(HEAT_MASS_G * SPECIFIC_HEAT_WATER * HEAT_DELTA_T, 3)} J`,
      `q = ${formatSig(heatEnergyKJ(HEAT_MASS_G, SPECIFIC_HEAT_WATER, HEAT_DELTA_T), 3)} kJ`,
    ],
    misconceptions: [
      { value: HEAT_MASS_G * SPECIFIC_HEAT_WATER * HEAT_DELTA_T, feedback: 'That is the energy in joules. Divide by 1000 to give kJ.' },
      { value: heatEnergyKJ(HEAT_MASS_G, SPECIFIC_HEAT_WATER, HEAT_END_C), feedback: 'You used the final temperature (80 °C) instead of the temperature change. q = m c ΔT needs ΔT = 80 − 20 = 60 K.' },
      { value: (HEAT_MASS_G * HEAT_DELTA_T) / J_PER_KJ, feedback: 'You left out the specific heat capacity. Multiply by c = 4.18 J g⁻¹ K⁻¹.' },
    ],
    takeaway: 'A change of 1 °C equals a change of 1 K, so ΔT can be found in either unit.',
  },
  {
    id: 'specific-heat-rise',
    kind: 'numeric',
    title: 'How hot does it get?',
    prompt: `A heater supplies ${RISE_ENERGY_KJ}.0 kJ to ${RISE_MASS_G} g of water. Assuming no energy is lost, by how many °C does the temperature rise?`,
    visual: 'specific-heat',
    answerLabel: 'ΔT',
    unit: '°C',
    answer: temperatureRiseK(RISE_ENERGY_KJ, RISE_MASS_G, SPECIFIC_HEAT_WATER),
    working: [
      `q = ${RISE_ENERGY_KJ}.0 kJ = ${RISE_ENERGY_KJ * J_PER_KJ} J`,
      `ΔT = q ÷ (m c) = ${RISE_ENERGY_KJ * J_PER_KJ} ÷ (${RISE_MASS_G} × 4.18)`,
      `ΔT = ${formatSig(temperatureRiseK(RISE_ENERGY_KJ, RISE_MASS_G, SPECIFIC_HEAT_WATER), 3)} °C`,
    ],
    misconceptions: [
      { value: RISE_ENERGY_KJ / (RISE_MASS_G * SPECIFIC_HEAT_WATER), feedback: 'You divided kJ by J g⁻¹ K⁻¹. Convert 10.0 kJ to 10 000 J first.' },
      { value: (RISE_ENERGY_KJ * J_PER_KJ * SPECIFIC_HEAT_WATER) / RISE_MASS_G, feedback: 'Rearrange q = m c ΔT carefully: ΔT = q ÷ (m × c). You multiplied by c instead of dividing.' },
    ],
    takeaway: 'Real heaters lose energy to the surroundings, so a measured rise is usually smaller than this model value.',
  },
  {
    id: 'coastal-climate',
    kind: 'choice',
    title: 'Coastal temperatures',
    prompt:
      'Coastal towns usually have a smaller day-to-night temperature range than inland towns at the same latitude. Which property of water best explains this?',
    visual: 'specific-heat',
    options: [
      { id: 'high-c', label: 'Water’s high specific heat capacity', correct: true, feedback: 'Yes. The sea absorbs energy by day and releases it at night with only small temperature changes, so it moderates the air temperature nearby.' },
      { id: 'salt', label: 'Salt in sea water absorbs heat', correct: false, feedback: 'Dissolved salt slightly lowers the specific heat capacity of sea water. The effect comes from the water itself and its hydrogen bonding.' },
      { id: 'density', label: 'Water is denser than air', correct: false, feedback: 'Density alone does not set how much energy is needed per degree. Specific heat capacity does: water needs 4.18 J to warm 1 g by 1 K.' },
      { id: 'boiling', label: 'Water has a high boiling point', correct: false, feedback: 'The sea is nowhere near boiling. The everyday effect comes from the energy needed to warm or cool water by a degree — its specific heat capacity.' },
    ],
    takeaway: 'The same property helps keep the temperature of lakes, oceans and living cells stable.',
  },
  {
    id: 'vaporisation-energy',
    kind: 'numeric',
    title: 'Boil away a sample',
    prompt: `How much energy, in kJ, is needed to vaporise ${VAPOUR_MASS_G}.0 g of water that is already at 100 °C? Use ΔvapH = 40.7 kJ mol⁻¹ and M(H₂O) = 18.02 g mol⁻¹.`,
    visual: 'vaporisation',
    answerLabel: 'q',
    unit: 'kJ',
    answer: vaporisationEnergyKJ(VAPOUR_MASS_G),
    working: [
      `n = m ÷ M = ${VAPOUR_MASS_G}.0 ÷ 18.02 = ${formatSig(VAPOUR_MASS_G / MOLAR_MASS_WATER, 4)} mol`,
      `q = n × ΔvapH = ${formatSig(VAPOUR_MASS_G / MOLAR_MASS_WATER, 4)} × 40.7`,
      `q = ${formatSig(vaporisationEnergyKJ(VAPOUR_MASS_G), 3)} kJ`,
    ],
    misconceptions: [
      { value: VAPOUR_MASS_G * VAPORISATION_ENTHALPY_WATER, feedback: 'ΔvapH is per mole, not per gram. Convert 36.0 g to moles first: n = 36.0 ÷ 18.02.' },
      { value: heatEnergyKJ(VAPOUR_MASS_G, SPECIFIC_HEAT_WATER, 100), feedback: 'q = m c ΔT applies to warming a liquid. Boiling happens at constant temperature, so use q = n × ΔvapH.' },
    ],
    takeaway:
      'Boiling 36 g of water takes about 81 kJ, but warming it from 0 °C to 100 °C takes only about 15 kJ. Separating molecules into a gas takes far more energy.',
  },
  {
    id: 'vaporisation-hydrogen-bonds',
    kind: 'choice',
    title: 'Why so much energy to boil?',
    prompt:
      'Water (40.7 kJ mol⁻¹) needs more than twice the energy per mole to vaporise than hydrogen sulfide (18.7 kJ mol⁻¹), although H₂S molecules have more electrons. Why?',
    visual: 'vaporisation',
    options: [
      { id: 'hydrogen-bonds', label: 'Hydrogen bonds between water molecules must be overcome', correct: true, feedback: 'Yes. Each water molecule can form up to four hydrogen bonds. Sulfur is not electronegative enough for H₂S to hydrogen bond, so it has only dipole–dipole and dispersion forces.' },
      { id: 'covalent', label: 'The O–H covalent bonds must break when water boils', correct: false, feedback: 'Boiling does not break covalent bonds — steam is still H₂O molecules. Only the forces between molecules are overcome.' },
      { id: 'heavier', label: 'Water molecules are heavier', correct: false, feedback: 'Water (18.02 g mol⁻¹) is lighter than H₂S (34.08 g mol⁻¹). On mass alone, water would boil more easily.' },
      { id: 'dispersion', label: 'Water has stronger dispersion forces', correct: false, feedback: 'H₂S has more electrons (18 vs 10), so its dispersion forces are stronger. The difference comes from hydrogen bonding.' },
    ],
    takeaway: 'Ethanol and ammonia also hydrogen bond and also have relatively high ΔvapH for their size.',
  },
  {
    id: 'hydride-trend',
    kind: 'choice',
    title: 'Water off the trend',
    prompt:
      'The boiling points of H₂S (−60 °C), H₂Se (−41 °C) and H₂Te (−2 °C) rise as the molecules get larger. If water had only dispersion and dipole–dipole forces, where would its boiling point lie?',
    visual: 'hydrides',
    options: [
      { id: 'below-h2s', label: 'Below −60 °C, lower than H₂S', correct: true, feedback: 'Yes. Following the trend, the smallest molecule would boil lowest — well below −60 °C. The real value, 100 °C, is anomalously high because of hydrogen bonding.' },
      { id: 'between', label: 'Between H₂S and H₂Se', correct: false, feedback: 'Water is the smallest of these molecules, so the trend puts it below H₂S, not between larger molecules.' },
      { id: 'above-h2te', label: 'Above H₂Te', correct: false, feedback: 'That is where water really is (100 °C), but only because of hydrogen bonding. Without it, the trend predicts the lowest boiling point.' },
      { id: 'same', label: 'At 100 °C, unchanged', correct: false, feedback: 'Remove hydrogen bonding and water would lose its strongest intermolecular force, so its boiling point would fall a long way.' },
    ],
    takeaway: 'Without hydrogen bonding, water would be a gas at room temperature and Earth would have no liquid oceans.',
  },
  {
    id: 'density-maximum',
    kind: 'choice',
    title: 'Densest liquid water',
    prompt: 'At what temperature is liquid water most dense?',
    visual: 'density',
    options: [
      { id: 'four', label: 'About 4 °C', correct: true, feedback: 'Yes. Liquid water reaches its maximum density, 0.99997 g mL⁻¹, at about 4 °C.' },
      { id: 'zero', label: '0 °C, the coldest liquid', correct: false, feedback: 'For most liquids the coldest sample is densest, but water is unusual. Below about 4 °C, hydrogen bonds start to hold molecules in a more open arrangement, so density falls again (0.99984 g mL⁻¹ at 0 °C).' },
      { id: 'twenty', label: 'About 20 °C', correct: false, feedback: 'At 20 °C the density is 0.99821 g mL⁻¹ — lower than at 4 °C because faster-moving molecules take up more space.' },
      { id: 'hundred', label: '100 °C', correct: false, feedback: 'Hot water is less dense: molecules move faster and spread out. That is why hot water rises in a heated pot.' },
    ],
    takeaway: 'This density maximum is a direct result of hydrogen bonding competing with thermal motion.',
  },
  {
    id: 'lake-winter',
    kind: 'choice',
    title: 'A frozen lake',
    prompt:
      'A still, deep lake has a layer of ice on top in winter. What is the temperature of the water at the bottom?',
    visual: 'density',
    options: [
      { id: 'four', label: 'About 4 °C', correct: true, feedback: 'Yes. Water at about 4 °C is densest, so it sinks to the bottom. Colder water and ice float above it, which lets aquatic life survive the winter.' },
      { id: 'zero', label: '0 °C', correct: false, feedback: 'Water at 0 °C is less dense than water at 4 °C, so it rises towards the ice instead of sinking.' },
      { id: 'below-zero', label: 'Below 0 °C', correct: false, feedback: 'Fresh liquid water at normal pressure freezes at 0 °C, and that layer floats. The bottom stays at about 4 °C in a deep lake.' },
      { id: 'air', label: 'The same as the air above', correct: false, feedback: 'The ice layer insulates the water below it. The densest water — about 4 °C — settles at the bottom.' },
    ],
    takeaway: 'Most substances freeze from the bottom up. Water freezes from the top down.',
  },
  {
    id: 'ice-floats',
    kind: 'choice',
    title: 'Why ice floats',
    prompt: 'Ice (0.9167 g mL⁻¹) is less dense than liquid water at 0 °C (0.99984 g mL⁻¹). Why?',
    visual: 'density',
    options: [
      { id: 'lattice', label: 'Hydrogen bonds hold the molecules in an open hexagonal lattice', correct: true, feedback: 'Yes. In ice each molecule hydrogen bonds to four neighbours in a fixed open arrangement with more empty space than in the liquid.' },
      { id: 'bubbles', label: 'Ice contains trapped air bubbles', correct: false, feedback: 'Clear, bubble-free ice still floats. The low density comes from how the molecules are arranged.' },
      { id: 'lighter', label: 'Molecules in ice are lighter', correct: false, feedback: 'Each H₂O molecule has the same mass in ice and in liquid water. The difference is how much space the molecules take up.' },
      { id: 'different', label: 'Ice is a different compound', correct: false, feedback: 'Freezing is a physical change. Ice is still H₂O; only the arrangement of molecules changes.' },
    ],
    takeaway: 'Most solids are denser than their liquids. Water is a rare exception.',
  },
  {
    id: 'ice-fraction',
    kind: 'numeric',
    title: 'How much ice is underwater?',
    prompt:
      'A block of ice floats in fresh water at 0 °C. What percentage of its volume is below the surface? (Floating fraction = ρ(ice) ÷ ρ(water).)',
    visual: 'density',
    answerLabel: 'Volume below surface',
    unit: '%',
    answer: ICE_FRACTION_PERCENT,
    working: [
      'fraction below = ρ(ice) ÷ ρ(water) = 0.9167 ÷ 0.99984',
      `fraction below = ${formatSig(ICE_FRACTION_PERCENT / PERCENT, 4)}`,
      `percentage = ${formatSig(ICE_FRACTION_PERCENT, 3)} %`,
    ],
    misconceptions: [
      { value: PERCENT - ICE_FRACTION_PERCENT, feedback: 'That is the part above the surface. The fraction below the surface equals ρ(ice) ÷ ρ(water).' },
      { value: ICE_FRACTION_PERCENT / PERCENT, feedback: 'That is the fraction. Multiply by 100 to give a percentage.' },
    ],
    takeaway: 'About 92 % of floating ice is under the water — the “tip of the iceberg” really is small.',
  },
];

export function waterPropertyCaseById(id: string): WaterPropertyCase | undefined {
  return WATER_PROPERTY_CASES.find((candidate) => candidate.id === id);
}

export interface WaterChoiceResult {
  correct: boolean;
  feedback: string;
}

/** Fails closed on an option id that the case does not offer. */
export function checkWaterChoice(item: WaterChoiceCase, optionId: string): WaterChoiceResult | null {
  const option = item.options.find((candidate) => candidate.id === optionId);
  if (!option) return null;
  return { correct: option.correct, feedback: option.feedback };
}

export function checkWaterNumeric(item: WaterNumericCase, answer: number): AnswerCheck {
  return checkNumericAnswer(
    answer,
    item.answer,
    item.misconceptions,
    `${item.answerLabel} = ${formatSig(item.answer, 3)} ${item.unit}. ${item.takeaway}`,
    item.unit,
  );
}

export const WATER_PROPERTY_BOUNDARIES: string[] = [
  'Values are textbook reference data at the stated conditions, not measurements from this page.',
  'Calculations assume no energy is lost to the surroundings and pure water.',
  'Group 16 hydride boiling points are rounded to the nearest degree.',
  'The heating explorer uses the Particle Model heating constants for ice, water and steam at 101.325 kPa and stops at 150 °C.',
  'The same-energy comparison starts every substance at 25 °C and stops at a liquid’s normal boiling point, or at 100 °C for a solid, because the specific heat capacities are values near 25 °C.',
];
